import React, { useState } from 'react'
import { ReportSetting } from '../types'
import * as Styled from '../TouristDisturbanceSettings.styled'

interface KeywordEditorProps {
  setting: ReportSetting
  onSave: (setting: ReportSetting) => void
  isSaving?: boolean
}

const KeywordEditor: React.FC<KeywordEditorProps> = ({ setting, onSave, isSaving = false }) => {
  const [localSetting, setLocalSetting] = useState<ReportSetting>({ ...setting })
  const [newPrimaryKeyword, setNewPrimaryKeyword] = useState('')
  const [newSecondaryKeyword, setNewSecondaryKeyword] = useState('')
  const [newTertiaryKeyword, setNewTertiaryKeyword] = useState('')

  const addKeyword = (keywordType: 'primary' | 'secondary' | 'tertiary', keyword: string) => {
    if (!keyword.trim()) return

    const updatedSetting = { ...localSetting }
    
    switch (keywordType) {
      case 'primary':
        if (!updatedSetting.primaryKeywords.includes(keyword.trim())) {
          updatedSetting.primaryKeywords = [...updatedSetting.primaryKeywords, keyword.trim()]
        }
        setNewPrimaryKeyword('')
        break
      case 'secondary':
        if (!updatedSetting.secondaryKeywords.includes(keyword.trim())) {
          updatedSetting.secondaryKeywords = [...updatedSetting.secondaryKeywords, keyword.trim()]
        }
        setNewSecondaryKeyword('')
        break
      case 'tertiary':
        if (updatedSetting.tertiaryKeywords && !updatedSetting.tertiaryKeywords.includes(keyword.trim())) {
          updatedSetting.tertiaryKeywords = [...updatedSetting.tertiaryKeywords, keyword.trim()]
        } else if (!updatedSetting.tertiaryKeywords) {
          updatedSetting.tertiaryKeywords = [keyword.trim()]
        }
        setNewTertiaryKeyword('')
        break
    }
    
    setLocalSetting(updatedSetting)
  }

  const removeKeyword = (keywordType: 'primary' | 'secondary' | 'tertiary', keyword: string) => {
    const updatedSetting = { ...localSetting }
    
    switch (keywordType) {
      case 'primary':
        updatedSetting.primaryKeywords = updatedSetting.primaryKeywords.filter(k => k !== keyword)
        break
      case 'secondary':
        updatedSetting.secondaryKeywords = updatedSetting.secondaryKeywords.filter(k => k !== keyword)
        break
      case 'tertiary':
        if (updatedSetting.tertiaryKeywords) {
          updatedSetting.tertiaryKeywords = updatedSetting.tertiaryKeywords.filter(k => k !== keyword)
        }
        break
    }
    
    setLocalSetting(updatedSetting)
  }

  const handleSave = () => {
    onSave(localSetting)
  }

  const renderKeywordSection = (
    title: string,
    keywords: string[],
    keywordType: 'primary' | 'secondary' | 'tertiary',
    newKeyword: string,
    setNewKeyword: (value: string) => void
  ) => (
    <Styled.KeywordSection>
      <Styled.KeywordSectionTitle>{title}</Styled.KeywordSectionTitle>
      <Styled.KeywordList>
        {keywords.map((keyword, index) => (
          <Styled.KeywordTag key={index}>
            <span>{keyword}</span>
            <Styled.RemoveButton
              onClick={() => removeKeyword(keywordType, keyword)}
              title="Kaldır"
              disabled={isSaving}
            >
              ×
            </Styled.RemoveButton>
          </Styled.KeywordTag>
        ))}
      </Styled.KeywordList>
      <Styled.AddKeywordForm>
        <Styled.KeywordInput
          type="text"
          value={newKeyword}
          onChange={(e) => setNewKeyword(e.target.value)}
          placeholder={`Yeni ${title.toLowerCase()} keyword ekle`}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              addKeyword(keywordType, newKeyword)
            }
          }}
          disabled={isSaving}
        />
        <Styled.AddButton
          onClick={() => addKeyword(keywordType, newKeyword)}
          disabled={!newKeyword.trim() || isSaving}
        >
          Ekle
        </Styled.AddButton>
      </Styled.AddKeywordForm>
    </Styled.KeywordSection>
  )

  return (
    <div>
      {renderKeywordSection(
        'Birincil Keywords',
        localSetting.primaryKeywords,
        'primary',
        newPrimaryKeyword,
        setNewPrimaryKeyword
      )}
      
      {renderKeywordSection(
        'İkincil Keywords',
        localSetting.secondaryKeywords,
        'secondary',
        newSecondaryKeyword,
        setNewSecondaryKeyword
      )}
      
      {renderKeywordSection(
        'Üçüncül Keywords',
        localSetting.tertiaryKeywords || [],
        'tertiary',
        newTertiaryKeyword,
        setNewTertiaryKeyword
      )}
      
      <Styled.ActionsContainer>
        <Styled.SaveButton 
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
        </Styled.SaveButton>
      </Styled.ActionsContainer>
    </div>
  )
}

export default KeywordEditor 